import React from 'react';
import { Tooltip } from 'react-tooltip';
import { CheckIcon, XMarkIcon, ClockIcon } from '@heroicons/react/24/outline';
import { BellAlertIcon, BellSlashIcon } from '@heroicons/react/24/solid';

const cn = (...c) => c.filter(Boolean).join(' ');

const EXTRA_TIMES = [
  { name: '10 min', value: 10 },
  { name: '15 min', value: 15 },
  { name: '20 min', value: 20 },
  { name: '25 min', value: 25 },
  { name: '30+ min', value: 30 },
];

const StatusActionsBar = ({
  orderStatus,
  selectedExtraTime,
  setSelectedExtraTime,
  isSendSms,
  setIsSendSms,
  onChangeOrderStatus,
}) => {
  const showConfirm = orderStatus === 0;
  const showReady = orderStatus === 10;
  const showComplete = orderStatus === 20;

  if (!showConfirm && !showReady && !showComplete) return null;

  const compactBar = !showConfirm;

  return (
    <section
      className={cn(
        'reveal flex flex-col gap-4 rounded-md border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-800',
        compactBar && 'ml-auto w-fit max-w-full',
      )}
      aria-label="Order actions"
    >
      {showConfirm && (
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <ClockIcon className="h-4 w-4 text-blue-600 dark:text-sky-400" />
            <p className="text-[0.65rem] font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
              Select prep time to confirm
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex flex-wrap items-center gap-1 rounded-full border border-slate-200 bg-slate-50 p-1 dark:border-slate-700 dark:bg-slate-900/50">
              {EXTRA_TIMES.map((time) => {
                const active = time.value === selectedExtraTime;
                return (
                  <button
                    key={time.value}
                    type="button"
                    onClick={() => {
                      setSelectedExtraTime(time.value);
                      onChangeOrderStatus(10, time.value);
                    }}
                    className={cn(
                      'rounded-full px-3 py-1.5 text-xs font-semibold tabular-nums transition',
                      active
                        ? 'bg-emerald-500 text-white shadow-sm'
                        : 'text-slate-600 hover:bg-white hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-slate-100',
                    )}
                  >
                    {time.name}
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              onClick={() => onChangeOrderStatus(30)}
              className="inline-flex h-9 items-center gap-1.5 rounded-full border border-rose-200 bg-rose-50 px-4 text-xs font-semibold text-rose-700 transition hover:bg-rose-100 active:scale-95 dark:border-rose-900/50 dark:bg-rose-900/20 dark:text-rose-300 dark:hover:bg-rose-900/40"
            >
              <XMarkIcon className="h-4 w-4" />
              Reject
            </button>
          </div>
        </div>
      )}

      {showReady && (
        <div className="flex flex-wrap items-center justify-end gap-3">
          <Tooltip id="sms-toggle" />
          <button
            type="button"
            data-tooltip-id="sms-toggle"
            data-tooltip-content={
              isSendSms
                ? 'A text message will be sent to the customer'
                : 'No text message will be sent'
            }
            onClick={() => setIsSendSms(!isSendSms)}
            className={cn(
              'inline-flex h-9 w-9 items-center justify-center rounded-full transition active:scale-95',
              isSendSms
                ? 'bg-emerald-500/10 text-emerald-600 dark:bg-emerald-400/10 dark:text-emerald-400'
                : 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400',
            )}
          >
            {isSendSms ? (
              <BellAlertIcon className="h-4 w-4" />
            ) : (
              <BellSlashIcon className="h-4 w-4" />
            )}
          </button>
          <button
            type="button"
            onClick={() => onChangeOrderStatus(20)}
            className="inline-flex h-10 items-center gap-1.5 rounded-full bg-emerald-500 px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-600 active:scale-95"
          >
            <CheckIcon className="h-4 w-4" />
            Mark as Ready
          </button>
        </div>
      )}

      {showComplete && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => onChangeOrderStatus(100)}
            className="inline-flex h-10 items-center gap-1.5 rounded-md bg-blue-600 px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 active:scale-95 dark:bg-sky-500 dark:hover:bg-sky-400"
          >
            <CheckIcon className="h-4 w-4" />
            Complete Order
          </button>
        </div>
      )}
    </section>
  );
};

export default StatusActionsBar;
